import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import React from 'react';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { scorecardTemplateStore } from './ScorecardTemplateStore';
import { ScorecardTemplateCreateDialog } from './components/ScorecardTemplateCreateDialog';
import { ListStore } from '../../../shared/stores/ListStore';
import { observer } from 'mobx-react';
import SettingsTableSkeleton from '../SettingsTableSkeleton';
import {
	rolesAllowed,
	USER_ROLES
} from '../../../shared/security/RolesAllowed';
import { STATE_LABELS, TEMPLATE_STATES } from './constants';
import { Switch } from '../../layout/form-fields/inputs/Switch';
import { dialogStore } from '../../layout/modals/DialogStore';
import { notificationStore } from '../../layout/notifications/NotificationStore';
import SettingsSubsectionHeader from '../SettingsSubsectionHeader';
import SettingsListToolbar from '../SettingsListToolbar';
import SettingsListEmptyState from '../SettingsListEmptyState';
import { getSettingsSectionLabel } from '../settingsSections';

@rolesAllowed([USER_ROLES.ADMIN])
@withRouter
@observer
export class ScorecardTemplates extends React.Component {
	state = {
		data: [],
		filterText: '',
		loading: true
	};

	constructor(props) {
		super(props);

		this.listStore = new ListStore(scorecardTemplateStore, {
			entityName: 'Scorecard Template',
			createModal: (
				<ScorecardTemplateCreateDialog
					key="create"
					handleSuccess={({ data }) => {
						this.props.history.push(
							`/ats/settings/scorecard-templates/${data.id}`
						);
					}}
				/>
			)
		});
	}

	componentDidMount() {
		this.search();
	}

	search(value = '') {
		this.setState({ loading: true });

		scorecardTemplateStore.getScorecardTemplates(value).then(result => {
			if (result.status === 'success' && result.data) {
				this.setState({ data: result.data, loading: false });
			}
		});
	}

	handleToggleEnable(id, enabled) {
		dialogStore.openDialog(
			{
				title: enabled
					? 'Enable Scorecard Template'
					: 'Disable Scorecard Template',
				message: enabled
					? 'Enabled scorecard templates can be used when adding a candidate scorecard. Are you sure you want to enable this scorecard template?'
					: 'Disabled scorecard templates cannot be used when adding a candidate scorecard. Are you sure you want to disable this scorecard template?',
				confirmLabel: enabled ? 'Enable' : 'Disable',
				confirmButtonClass: enabled ? 'is-success' : 'is-danger'
			},
			() => {
				scorecardTemplateStore
					.update(id, {
						scorecardTemplate: {
							disabled: !enabled
						}
					})
					.then(() => {
						this.search();
					});
			}
		);
	}

	hasFilters = () => Boolean(this.state.filterText.trim());

	clearFilters = () => {
		this.setState({ filterText: '' }, () => {
			this.search();
		});
	};

	getMenuItems(row) {
		const isInUse = row.usageCount > 0;

		return [
			{
				label: 'Edit',
				img: '/ats/icons/edit-icon.svg',
				onClick: () =>
					this.props.history.push(`/ats/settings/scorecard-templates/${row.id}`)
			},
			{
				label: 'Delete',
				img: '/ats/icons/trash-icon.svg',
				className: 'is-danger',
				disabled: isInUse,
				tooltip: isInUse
					? 'This Scorecard Template is currently in use.'
					: null,
				onClick: () =>
					dialogStore.openDialog(
						{
							title: 'Delete Scorecard Template',
							message:
								'Are you sure you want to delete this Scorecard Template?'
						},
						() =>
							this.listStore.delete(
								row,
								this.onDeleteSuccess,
								this.onDeleteFail,
								true
							)
					)
			}
		];
	}

	render() {
		const { data, loading, filterText } = this.state;
		const isEmpty = !loading && data.length === 0;

		return (
			<div className="ScorecardTemplates container">
				<SettingsSubsectionHeader
					title={getSettingsSectionLabel('scorecard-templates')}
					actions={
						<SettingsListToolbar
							searchValue={filterText}
							onSearchChange={value => {
								this.search(value);
								this.setState({ filterText: value });
							}}
							createLabel={`Create ${this.listStore.modalsConfigs.entityName}`}
							onCreate={() => this.listStore.handleCreate()}
						/>
					}
				/>
				<div className="columns">
					<div className="column is-12 table-wrapper">
						<table className="table is-fullwidth">
							<thead>
								<tr>
									<th className="column-header-name">Name</th>
									<th className="column-header-name">Status</th>
									<th className="column-header-name">Published At</th>
									<th className="enabled-column">Enabled</th>
									<th className="actions-column" />
								</tr>
							</thead>
							{loading ? (
								<SettingsTableSkeleton columns={5} />
							) : !isEmpty ? (
								<tbody>
									{data.map(row => {
										const isPublished =
											row.status === TEMPLATE_STATES.PUBLISHED;

										return (
											<tr key={row.id}>
												<td className="has-text-weight-bold column-name">
													{row.name}
												</td>

												<td className="column-status">
													{STATE_LABELS[row.status]}
												</td>

												<td className="column-publishedAt">
													{isPublished
														? moment(row.publishedAt).format('lll')
														: '-'}
												</td>

												<td className="enabled-column">
													<Switch
														key={`disable-${row.id}`}
														id={`disable-${row.id}`}
														value={!row.disabled}
														onValueChange={enabled => {
															this.handleToggleEnable(row.id, enabled);
														}}
													/>
												</td>

												<td className="actions-column">
													<HoverMenu
														id={row.id}
														position="left"
														buttonIcon={
															<img
																src="/ats/icons/three-dots-icon.svg"
																alt="More actions"
															/>
														}
														items={this.getMenuItems(row)}
													/>
												</td>
											</tr>
										);
									})}
								</tbody>
							) : null}
						</table>
						{!loading && isEmpty ? (
							<SettingsListEmptyState
								entityPlural="scorecard templates"
								hasFilters={this.hasFilters()}
								onClearFilters={this.clearFilters}
							/>
						) : null}
					</div>
				</div>
			</div>
		);
	}

	onDeleteSuccess = item => {
		this.search();
		this.setState({ filterText: '' });
	};

	onDeleteFail = (item, result) => {
		notificationStore.pushErrorNotification(
			'An error has occurred deleting this Scorecard Template.',
			result.message
		);
	};
}
